import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { JobCard } from "@/components/JobCard";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState, Loading } from "@/components/EmptyState";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Briefcase, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function MyJobs() {
  const { mode } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (mode === "employer") setJobs((await api.get("/jobs/mine/employer")).data);
        else setApps((await api.get("/applications/mine")).data);
      } finally { setLoading(false); }
    })();
  }, [mode]);

  if (loading) return <Loading />;

  if (mode === "employer") {
    const groups = {
      Aktif: jobs.filter((j) => ["OPEN", "ASSIGNED", "IN_PROGRESS"].includes(j.status)),
      Draft: jobs.filter((j) => j.status === "DRAFT"),
      Selesai: jobs.filter((j) => j.status === "COMPLETED"),
      Batal: jobs.filter((j) => j.status === "CANCELLED"),
    };
    return (
      <div data-testid="my-jobs-employer">
        <h1 className="text-2xl sm:text-3xl font-extrabold mb-5">Pekerjaan Saya</h1>
        <Tabs defaultValue="Aktif">
          <TabsList className="mb-5 flex-wrap h-auto">
            {Object.keys(groups).map((k) => (
              <TabsTrigger key={k} value={k} data-testid={`tab-${k}`}>{k} ({groups[k].length})</TabsTrigger>
            ))}
          </TabsList>
          {Object.entries(groups).map(([k, list]) => (
            <TabsContent key={k} value={k}>
              {list.length === 0 ? (
                <EmptyState icon={Briefcase} title={`Belum ada pekerjaan ${k.toLowerCase()}`} actionLabel="+ Buat Pekerjaan" onAction={() => navigate("/buat")} />
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {list.map((j, i) => (
                    <JobCard key={j.id} job={j} index={i} showStatus
                      footer={j.pending_count > 0 && <div className="text-sm font-semibold text-amber-600 pt-1">{j.pending_count} pelamar menunggu</div>} />
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    );
  }

  // Worker
  const groups = {
    Lamaran: apps.filter((a) => a.status === "PENDING"),
    Diterima: apps.filter((a) => a.status === "ACCEPTED" && a.job?.status !== "COMPLETED"),
    Selesai: apps.filter((a) => a.job?.status === "COMPLETED"),
    Ditolak: apps.filter((a) => ["REJECTED", "CANCELLED"].includes(a.status)),
  };
  return (
    <div data-testid="my-jobs-worker">
      <h1 className="text-2xl sm:text-3xl font-extrabold mb-5">Pekerjaan Saya</h1>
      <Tabs defaultValue="Lamaran">
        <TabsList className="mb-5 flex-wrap h-auto">
          {Object.keys(groups).map((k) => (
            <TabsTrigger key={k} value={k} data-testid={`tab-${k}`}>{k} ({groups[k].length})</TabsTrigger>
          ))}
        </TabsList>
        {Object.entries(groups).map(([k, list]) => (
          <TabsContent key={k} value={k}>
            {list.length === 0 ? (
              <EmptyState icon={Briefcase} title={`Belum ada ${k.toLowerCase()}`} actionLabel="Cari Pekerjaan" onAction={() => navigate("/cari")} />
            ) : (
              <div className="space-y-3">
                {list.map((a) => (
                  <div key={a.id} data-testid={`myapp-${a.id}`} onClick={() => navigate(`/pekerjaan/${a.job_id}`)}
                    className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between hover:border-slate-300 cursor-pointer transition-colors">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 truncate">{a.job?.title || "Pekerjaan"}</p>
                      <p className="text-sm text-slate-500 flex items-center gap-1"><MapPin size={13} /> {a.job?.city}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {a.job && a.status !== "PENDING" && (
                        <span className="text-xs text-slate-400">{a.job.status === "COMPLETED" ? "Selesai" : ""}</span>
                      )}
                      <StatusBadge status={a.status} kind="app" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
